import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  getStats,
  getUsers,
  getAdminBooks,
  getAdminPoems,
  getAdminStories,
  createBook,
  createChapter,
  createPoem,
  createStory,
  updateBook,
  updateChapter,
  updatePoem,
  updateStory,
  deleteBook,
  deletePoem,
  deleteStory,
  getChapters,
  getChapter,
  deleteChapter,
  uploadFile,
} from '../../services/api';
import './Dashboard.css';

// Uploaded covers are stored as paths relative to the API origin (e.g.
// "/uploads/covers/x.jpg"). Build a displayable URL from the configured API base.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api\/?$/, '');
const coverSrc = (path: string) =>
  /^https?:\/\//.test(path) ? path : `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;

type Tab = 'overview' | 'books' | 'drafts' | 'poems' | 'stories' | 'users';

interface Stats {
  total_users: number;
  total_books: number;
  total_poems: number;
  total_stories: number;
  total_drafts: number;
  total_threads: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [books, setBooks] = useState<any[]>([]);
  const [poems, setPoems] = useState<any[]>([]);
  const [stories, setStories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<'book' | 'chapter' | 'poem' | 'story'>('book');
  const [formData, setFormData] = useState<any>({});
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);
  // When set, the form edits an existing item instead of creating a new one.
  const [editId, setEditId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  // Chapter management (per book)
  const [expandedBookId, setExpandedBookId] = useState<number | null>(null);
  const [chaptersByBook, setChaptersByBook] = useState<Record<number, any[]>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, booksRes, poemsRes, storiesRes] = await Promise.all([
        getStats(),
        getUsers(),
        getAdminBooks(),
        getAdminPoems(),
        getAdminStories(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setBooks(booksRes.data);
      setPoems(poemsRes.data);
      setStories(storiesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openForm = (type: 'book' | 'chapter' | 'poem' | 'story', bookId?: number) => {
    setFormType(type);
    setFormData({});
    setSelectedBookId(bookId || null);
    setEditId(null);
    setShowForm(true);
  };

  const openEditForm = (type: 'book' | 'poem' | 'story', item: any) => {
    setFormType(type);
    setFormData({ ...item });
    setSelectedBookId(null);
    setEditId(item.id);
    setShowForm(true);
  };

  // Chapters list their content stripped, so fetch the full chapter before editing.
  const openEditChapter = async (bookId: number, chapterId: number) => {
    try {
      const res = await getChapter(bookId, chapterId);
      setFormType('chapter');
      setFormData({ ...res.data });
      setSelectedBookId(bookId);
      setEditId(chapterId);
      setShowForm(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCoverUpload = async (file: File) => {
    setUploading(true);
    try {
      const res = await uploadFile(file, 'covers');
      setFormData((prev: any) => ({ ...prev, cover_image: res.data.url }));
    } catch (err) {
      console.error(err);
      window.alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const submitWith = async (published: boolean) => {
    // When editing, preserve the item's current published state instead of
    // letting the submit button flip it.
    const pub = editId !== null ? !!formData.published : published;
    try {
      switch (formType) {
        case 'book': {
          const payload = {
            title: formData.title,
            description: formData.description,
            cover_image: formData.cover_image || '',
            author: formData.author || 'Kutloano Mkhulisi',
            year: parseInt(formData.year) || new Date().getFullYear(),
            genre: formData.genre || 'Fiction',
            published: pub,
          };
          if (editId !== null) await updateBook(editId, payload);
          else await createBook(payload);
          break;
        }
        case 'chapter':
          if (editId !== null) {
            await updateChapter(editId, {
              title: formData.title,
              content: formData.content,
              order_num: parseInt(formData.order_num) || 1,
            });
            if (selectedBookId) await loadChapters(selectedBookId);
          } else if (selectedBookId) {
            await createChapter(selectedBookId, {
              title: formData.title,
              content: formData.content,
              order_num: parseInt(formData.order_num) || 1,
            });
            setExpandedBookId(selectedBookId);
            await loadChapters(selectedBookId);
          }
          break;
        case 'poem': {
          const payload = {
            title: formData.title,
            content: formData.content,
            category: formData.category || 'General',
            published: pub,
          };
          if (editId !== null) await updatePoem(editId, payload);
          else await createPoem(payload);
          break;
        }
        case 'story': {
          const payload = {
            title: formData.title,
            content: formData.content,
            synopsis: formData.synopsis || '',
            cover_image: formData.cover_image || '',
            published: pub,
          };
          if (editId !== null) await updateStory(editId, payload);
          else await createStory(payload);
          break;
        }
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWith(true);
  };

  const handleSaveDraft = async () => {
    if (!formData.title) {
      window.alert('Add a title before saving as draft.');
      return;
    }
    await submitWith(false);
  };

  const togglePublish = async (type: 'book' | 'poem' | 'story', item: any) => {
    try {
      const next = { ...item, published: !item.published };
      switch (type) {
        case 'book': await updateBook(item.id, next); break;
        case 'poem': await updatePoem(item.id, next); break;
        case 'story': await updateStory(item.id, next); break;
      }
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (type: string, id: number) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      switch (type) {
        case 'book': await deleteBook(id); break;
        case 'poem': await deletePoem(id); break;
        case 'story': await deleteStory(id); break;
      }
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const loadChapters = async (bookId: number) => {
    try {
      const res = await getChapters(bookId);
      setChaptersByBook((prev) => ({ ...prev, [bookId]: res.data }));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleChapters = (bookId: number) => {
    if (expandedBookId === bookId) {
      setExpandedBookId(null);
      return;
    }
    setExpandedBookId(bookId);
    if (!chaptersByBook[bookId]) {
      loadChapters(bookId);
    }
  };

  const handleDeleteChapter = async (bookId: number, chapterId: number) => {
    if (!window.confirm('Delete this chapter? This cannot be undone.')) return;
    try {
      await deleteChapter(chapterId);
      await loadChapters(bookId);
    } catch (err) {
      console.error(err);
    }
  };

  const draftItems = [
    ...books.filter((b) => !b.published).map((b) => ({ ...b, _type: 'book' as const })),
    ...poems.filter((p) => !p.published).map((p) => ({ ...p, _type: 'poem' as const })),
    ...stories.filter((s) => !s.published).map((s) => ({ ...s, _type: 'story' as const })),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'drafts', label: `Drafts${draftItems.length ? ` (${draftItems.length})` : ''}` },
    { key: 'books', label: 'Books' },
    { key: 'poems', label: 'Poems' },
    { key: 'stories', label: 'Stories' },
    { key: 'users', label: 'Users' },
  ];

  if (loading) {
    return (
      <div className="loading-container" style={{ paddingTop: 120 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-layout">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <div className="sidebar-user">
            <div className="user-avatar">
              <span>{user?.username?.charAt(0) || 'K'}</span>
            </div>
            <div className="user-info">
              <h4>{user?.username}</h4>
              <span>Administrator</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`sidebar-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <div className="dashboard-header">
            <h1>Dashboard</h1>
            <p>Manage your content and track your audience</p>
          </div>

          {activeTab === 'overview' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(201, 168, 76, 0.1)', color: 'var(--color-primary)' }}>&#128214;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_books || 0}</span>
                    <span className="stat-card-label">Books</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(233, 69, 96, 0.1)', color: 'var(--color-accent)' }}>&#9998;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_poems || 0}</span>
                    <span className="stat-card-label">Poems</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(78, 205, 196, 0.1)', color: 'var(--color-success)' }}>&#128220;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_stories || 0}</span>
                    <span className="stat-card-label">Stories</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>&#128221;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_drafts || 0}</span>
                    <span className="stat-card-label">Drafts</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>&#128172;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_threads || 0}</span>
                    <span className="stat-card-label">Forum Threads</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-card-icon" style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' }}>&#128101;</div>
                  <div className="stat-card-info">
                    <span className="stat-card-number">{stats?.total_users || 0}</span>
                    <span className="stat-card-label">Users</span>
                  </div>
                </div>
              </div>

              <div className="dashboard-section">
                <h2>Recent Users</h2>
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice(0, 5).map((u) => (
                        <tr key={u.id}>
                          <td>{u.username}</td>
                          <td>{u.email}</td>
                          <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'drafts' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="dashboard-section">
                <h2>Drafts &mdash; not yet live</h2>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginBottom: 16 }}>
                  Work in progress. Readers can't see these until you publish them.
                </p>
              </div>
              {draftItems.length === 0 ? (
                <div className="data-table" style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  No drafts yet. Save any new book, poem, or story as a draft to keep it private.
                </div>
              ) : (
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Title</th>
                        <th>Preview</th>
                        <th>Saved</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftItems.map((d: any) => (
                        <tr key={`${d._type}-${d.id}`}>
                          <td><span className="role-badge reader">{d._type}</span></td>
                          <td><strong>{d.title}</strong></td>
                          <td className="synopsis-cell">
                            {d._type === 'book' ? d.description : (d.synopsis || d.content?.slice(0, 120))}
                          </td>
                          <td>{new Date(d.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="table-actions">
                              <button className="btn-sm btn-primary-sm" onClick={() => togglePublish(d._type, d)}>
                                Publish
                              </button>
                              <button className="btn-sm btn-outline" onClick={() => openEditForm(d._type, d)}>
                                Edit
                              </button>
                              <button className="btn-sm btn-danger" onClick={() => handleDelete(d._type, d.id)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'books' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="dashboard-actions">
                <button className="btn btn-primary" onClick={() => openForm('book')}>
                  + Add Book
                </button>
              </div>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Genre</th>
                      <th>Year</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {books.map((b) => (
                      <React.Fragment key={b.id}>
                        <tr>
                          <td><strong>{b.title}</strong></td>
                          <td>{b.author}</td>
                          <td>{b.genre}</td>
                          <td>{b.year}</td>
                          <td>
                            <span className={`status-badge ${b.published ? 'published' : 'draft'}`}>
                              {b.published ? 'Live' : 'Draft'}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button className="btn-sm btn-outline" onClick={() => toggleChapters(b.id)}>
                                {expandedBookId === b.id ? 'Hide Chapters' : 'Chapters'}
                              </button>
                              <button className="btn-sm btn-outline" onClick={() => openForm('chapter', b.id)}>
                                + Chapter
                              </button>
                              <button className="btn-sm btn-outline" onClick={() => openEditForm('book', b)}>
                                Edit
                              </button>
                              <button className="btn-sm btn-outline" onClick={() => togglePublish('book', b)}>
                                {b.published ? 'Unpublish' : 'Publish'}
                              </button>
                              <button className="btn-sm btn-danger" onClick={() => handleDelete('book', b.id)}>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedBookId === b.id && (
                          <tr className="chapter-row">
                            <td colSpan={6}>
                              {!chaptersByBook[b.id] ? (
                                <p className="chapter-empty">Loading chapters&hellip;</p>
                              ) : chaptersByBook[b.id].length === 0 ? (
                                <p className="chapter-empty">No chapters yet. Use &ldquo;+ Chapter&rdquo; to add one.</p>
                              ) : (
                                <ul className="chapter-list">
                                  {chaptersByBook[b.id]
                                    .slice()
                                    .sort((c1: any, c2: any) => c1.order_num - c2.order_num)
                                    .map((ch: any) => (
                                      <li key={ch.id} className="chapter-list-item">
                                        <span className="chapter-list-title">
                                          <span className="chapter-list-num">Ch. {ch.order_num}</span>
                                          {ch.title}
                                        </span>
                                        <div className="table-actions">
                                          <button
                                            className="btn-sm btn-outline"
                                            onClick={() => openEditChapter(b.id, ch.id)}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="btn-sm btn-danger"
                                            onClick={() => handleDeleteChapter(b.id, ch.id)}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'poems' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="dashboard-actions">
                <button className="btn btn-primary" onClick={() => openForm('poem')}>
                  + Add Poem
                </button>
              </div>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poems.map((p) => (
                      <tr key={p.id}>
                        <td><strong>{p.title}</strong></td>
                        <td>{p.category}</td>
                        <td>
                          <span className={`status-badge ${p.published ? 'published' : 'draft'}`}>
                            {p.published ? 'Live' : 'Draft'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="btn-sm btn-outline" onClick={() => openEditForm('poem', p)}>
                              Edit
                            </button>
                            <button className="btn-sm btn-outline" onClick={() => togglePublish('poem', p)}>
                              {p.published ? 'Unpublish' : 'Publish'}
                            </button>
                            <button className="btn-sm btn-danger" onClick={() => handleDelete('poem', p.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'stories' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="dashboard-actions">
                <button className="btn btn-primary" onClick={() => openForm('story')}>
                  + Add Story
                </button>
              </div>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Synopsis</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stories.map((s) => (
                      <tr key={s.id}>
                        <td><strong>{s.title}</strong></td>
                        <td className="synopsis-cell">{s.synopsis}</td>
                        <td>
                          <span className={`status-badge ${s.published ? 'published' : 'draft'}`}>
                            {s.published ? 'Live' : 'Draft'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="btn-sm btn-outline" onClick={() => openEditForm('story', s)}>
                              Edit
                            </button>
                            <button className="btn-sm btn-outline" onClick={() => togglePublish('story', s)}>
                              {s.published ? 'Unpublish' : 'Publish'}
                            </button>
                            <button className="btn-sm btn-danger" onClick={() => handleDelete('story', s.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'users' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="dashboard-section">
                <h2>All Registered Users</h2>
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id}>
                          <td>{u.id}</td>
                          <td>{u.username}</td>
                          <td>{u.email}</td>
                          <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </main>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="form-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-modal-header">
              <h2>
                {formType === 'book' && (editId !== null ? 'Edit Book' : 'Add New Book')}
                {formType === 'chapter' && (editId !== null ? 'Edit Chapter' : 'Add New Chapter')}
                {formType === 'poem' && (editId !== null ? 'Edit Poem' : 'Add New Poem')}
                {formType === 'story' && (editId !== null ? 'Edit Story' : 'Add New Story')}
              </h2>
              <button className="form-modal-close" onClick={() => setShowForm(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  className="form-input"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              {formType === 'book' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-input form-textarea"
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Author</label>
                      <input
                        className="form-input"
                        value={formData.author || ''}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        placeholder="Kutloano Mkhulisi"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Year</label>
                      <input
                        className="form-input"
                        type="number"
                        value={formData.year || ''}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        placeholder="2024"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Genre</label>
                    <input
                      className="form-input"
                      value={formData.genre || ''}
                      onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                      placeholder="Fiction"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cover Image</label>
                    {formData.cover_image && (
                      <img className="cover-preview" src={coverSrc(formData.cover_image)} alt="Cover preview" />
                    )}
                    <input
                      className="form-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleCoverUpload(f);
                      }}
                    />
                    {uploading && <span className="upload-status">Uploading&hellip;</span>}
                  </div>
                </>
              )}

              {formType === 'chapter' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Chapter Number</label>
                    <input
                      className="form-input"
                      type="number"
                      value={formData.order_num || ''}
                      onChange={(e) => setFormData({ ...formData, order_num: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Content</label>
                    <textarea
                      className="form-input form-textarea"
                      style={{ minHeight: 300 }}
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}

              {formType === 'poem' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input
                      className="form-input"
                      value={formData.category || ''}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Freedom, Love, Empowerment"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Content</label>
                    <textarea
                      className="form-input form-textarea"
                      style={{ minHeight: 250 }}
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Write your poem here... Use line breaks for stanzas."
                      required
                    />
                  </div>
                </>
              )}

              {formType === 'story' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Synopsis</label>
                    <input
                      className="form-input"
                      value={formData.synopsis || ''}
                      onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
                      placeholder="Brief summary of the story"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cover Image</label>
                    {formData.cover_image && (
                      <img className="cover-preview" src={coverSrc(formData.cover_image)} alt="Cover preview" />
                    )}
                    <input
                      className="form-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleCoverUpload(f);
                      }}
                    />
                    {uploading && <span className="upload-status">Uploading&hellip;</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Content</label>
                    <textarea
                      className="form-input form-textarea"
                      style={{ minHeight: 300 }}
                      value={formData.content || ''}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}

              <div className="form-actions">
                {editId === null && formType !== 'chapter' && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleSaveDraft}
                  >
                    Save as Draft
                  </button>
                )}
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {editId !== null
                    ? 'Save Changes'
                    : formType === 'chapter'
                    ? 'Save Chapter'
                    : 'Publish Now'}
                </button>
              </div>
              {editId === null && formType !== 'chapter' && (
                <p style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                  Drafts stay private &mdash; only you can see them until you publish.
                </p>
              )}
              {editId !== null && formType !== 'chapter' && (
                <p style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                  Editing keeps the current {formData.published ? 'published' : 'draft'} status.
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
